import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { TaskTypeResponse } from '@erp/rpc';
import {
  useTaskType,
  useUpdateTaskType,
} from '@/api/rpc/hooks/tasks/useTaskTypes';
import { Button } from '@/components/ui/button';
import { XIcon } from '@phosphor-icons/react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from 'src/context/ToastContext';
import { TaskTypeDefaultAssigneeControls } from '@/pages/authenticated/settings/organization/components/cards/task-types/TaskTypeDefaultAssigneeField';
import {
  buildTaskTypeDefaultAssignee,
  isDefaultAssigneeComplete,
} from '@/pages/authenticated/settings/organization/components/cards/task-types/taskTypeAdmin';
import {
  assigneeDraftFor,
  changedAssigneeDrafts,
  type AssigneeDrafts,
} from './arTasksAssignmentRules';

/** One kind of AR task: its name, the records it covers, who gets it. */
function AssignmentRuleCard({
  taskType,
  entityLabel,
  drafts,
  disabled,
  onChange,
}: {
  taskType: TaskTypeResponse | undefined;
  entityLabel: string;
  drafts: AssigneeDrafts;
  disabled: boolean;
  onChange: (taskTypeId: string, next: AssigneeDrafts[string]) => void;
}) {
  if (!taskType) return <Skeleton className="h-28 w-full" />;
  return (
    <section className="space-y-3 rounded-lg border p-3">
      <div>
        <h3 className="text-sm font-semibold">{taskType.name}</h3>
        <p className="text-xs text-muted-foreground">{entityLabel}</p>
      </div>
      <TaskTypeDefaultAssigneeControls
        inline
        editor={assigneeDraftFor(taskType, drafts)}
        disabled={disabled}
        onChange={(next) => onChange(taskType.id, next)}
      />
    </section>
  );
}

/**
 * Who gets each AR billing task when it opens. Opens over the AR Tasks page
 * so nobody leaves it. Each rule is the task type's default assignee; edits
 * stay drafts until Save writes the changed ones.
 */
export function ArTasksAssignmentRulesSheet({
  open,
  onOpenChange,
  taskTypeIds,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskTypeIds: { toInvoice: string | null; toSend: string | null };
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const updateTaskType = useUpdateTaskType();
  const toInvoice = useTaskType(taskTypeIds.toInvoice);
  const toSend = useTaskType(taskTypeIds.toSend);
  const [drafts, setDrafts] = useState<AssigneeDrafts>({});
  const hasTypes = taskTypeIds.toInvoice != null || taskTypeIds.toSend != null;
  const loaded = [toInvoice.data, toSend.data].filter(
    (taskType): taskType is TaskTypeResponse => taskType != null,
  );
  const changed = changedAssigneeDrafts(loaded, drafts);
  const saving = updateTaskType.isPending;

  function close() {
    setDrafts({});
    onOpenChange(false);
  }

  async function save() {
    try {
      await Promise.all(
        changed.map(({ taskType, editor }) =>
          updateTaskType.mutateAsync({
            id: taskType.id,
            ...buildTaskTypeDefaultAssignee(editor),
          }),
        ),
      );
      showToast({
        color: 'success',
        title: gt('Assignment rules saved', {
          context: 'toast after saving AR task assignment rules',
        }),
      });
      close();
    } catch (err) {
      showToast({
        color: 'error',
        title: gt('Could not save assignment rules', {
          context: 'toast when saving AR task assignment rules fails',
        }),
        message: err instanceof Error ? err.message : '',
      });
    }
  }

  function setDraft(taskTypeId: string, next: AssigneeDrafts[string]) {
    setDrafts((current) => ({ ...current, [taskTypeId]: next }));
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <SheetContent
        side="right"
        hideCloseButton
        className="flex w-full flex-col gap-0 sm:max-w-xl"
      >
        <SheetHeader className="flex-row items-start justify-between gap-2 border-b px-5 pb-3 pt-4">
          <div className="flex flex-col gap-1">
            <SheetTitle>
              <T context="sheet title, AR task assignment rules">
                Assignment Rules
              </T>
            </SheetTitle>
            <SheetDescription>
              <T context="sheet description, AR task assignment rules">
                Who gets each kind of accounts receivable task when it opens.
                Applies to new tasks.
              </T>
            </SheetDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={close}
          >
            <span className="sr-only">
              <T context="button, close AR task assignment rules">Close</T>
            </span>
            <XIcon className="size-4" />
          </Button>
        </SheetHeader>
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {!hasTypes && (
            <p className="text-sm text-muted-foreground">
              <T context="AR task assignment rules, task types not created yet">
                Accounts receivable task types are created after the next
                accounts receivable sync.
              </T>
            </p>
          )}
          {taskTypeIds.toInvoice && (
            <AssignmentRuleCard
              taskType={toInvoice.data ?? undefined}
              entityLabel={gt('Sales orders', {
                context: 'AR task assignment rules, records this type covers',
              })}
              drafts={drafts}
              disabled={saving}
              onChange={setDraft}
            />
          )}
          {taskTypeIds.toSend && (
            <AssignmentRuleCard
              taskType={toSend.data ?? undefined}
              entityLabel={gt('Invoices', {
                context: 'AR task assignment rules, records this type covers',
              })}
              drafts={drafts}
              disabled={saving}
              onChange={setDraft}
            />
          )}
        </div>
        <SheetFooter className="flex-row justify-end gap-2 border-t px-5 py-3">
          <Button variant="outline" onClick={close} disabled={saving}>
            <T context="button, discard AR task assignment rule edits">
              Cancel
            </T>
          </Button>
          <Button
            onClick={() => void save()}
            disabled={
              saving ||
              changed.length === 0 ||
              !changed.every(({ editor }) => isDefaultAssigneeComplete(editor))
            }
          >
            {saving && <Spinner className="mr-2 h-4 w-4" />}
            <T context="button, save AR task assignment rules">Save</T>
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
